<template>
  <div v-if="eventCodeMap">
    <BRow>
      <BCol class="text-center">
        <BButtonGroup size="sm" class="mb-2">
          <BButton :variant="eventColor[evtCode]" pill>
            <BIconBellSlashFill class="mr-1"/> {{ eventCodeMap[evtCode] }} | {{ evtCode }} <b>알람 확인/지연 설정</b>
          </BButton>
        </BButtonGroup>
      </BCol>
    </BRow>

    <BRow v-for="(e,i) in eventTanks" :key="i">
      <BCol>
        <BInputGroup size="sm" class="small mb-1">
          <BButtonGroup size="sm" class="mr-1">
            <BButton variant="outline-info" class="font-sm small" style="min-width:70px">{{ tankMap[e.tid]?.name }}</BButton>
          </BButtonGroup>
          <BFormSelect size="sm"
                       v-model="suppressAlarmMin"
                       :options="suspendOpts"
           class="theme-select"/>
          <BInputGroupAppend>
            <BButton size="sm" variant="outline-warning" @click="setSuppressAlarm(e.tid, evtCode, suppressAlarmMin)">
              알람지연
            </BButton>
          </BInputGroupAppend>
        </BInputGroup>
      </BCol>
    </BRow>

    <BRow>
      <BCol>
        <BInputGroup size="sm">
          <BFormSelect size="sm"
                       v-model="suppress.tid"
                       :options="tankOpts" class="theme-select"/>
          <BFormSelect size="sm"
                       v-model="suppress.min"
                       :options="suspendOpts" class="theme-select"/>
          <BInputGroupAppend size="sm">
            <BButton size="sm"
                     variant="outline-warning"
                     @click="setSuppressAlarm(suppress.tid, evtCode, suppress.min)">알람지연</BButton>
          </BInputGroupAppend>
        </BInputGroup>
      </BCol>
    </BRow>

    <BTableSimple class="small mt-1" :style="{ minHeight: tableMinHeight }" small bordered sticky-header>
      <BThead class="text-center bg-gradient-dark">
        <BTh width="80">경보</BTh>
        <BTh>알람 지연</BTh>
      </BThead>
      <BTbody>
      <BTr v-for="(obj,k) in visibleSuppressAlarms" :key="k">
        <BTd class="text-center">
          <BBadge class="font-sm" :variant="eventColor[k]">
            {{ eventCodeMap[k].replace('경보','') }}
          </BBadge>
        </BTd>
        <BTd>
          <BRow v-for="(dt,tid) in obj" :key="tid">
            <BCol cols="5"><BBadge class="font-sm" variant="light">{{ tankMap[tid]?.name }}</BBadge></BCol>
            <BCol class="ml-1">
              <BBadge class="font-sm" variant="dark">{{ `~ ${toShortTime(dt)}` }}</BBadge>
            </BCol>
          </BRow>
        </BTd>
      </BTr>
      </BTBody>
    </BTableSimple>
  </div>
</template>

<script>
import '@/common/HelperMixin';
import moment from "moment";
import qs from 'querystring';
import { apiCall, sleep, loadSuppressAlarms } from '@/common/utils';
import { EVENT_COLOR } from '@/common/constants';

// 경보판(경보현황·경보지연 설정)에서 감추는 경보 코드: 8 센서점검
// 서버 코드맵(EVENT)은 8 센서점검, 9 설정점검 이며, 서버(core/PolCore.js)는 점검류 경보를
// 모두 9 로만 내보낸다. 8 은 코드맵에만 있고 실제로 발생하지 않는다.
const HIDDEN_EVENT_CODE = '8';

export default {
  name: 'AlarmDelayPanel',
  props: {
    // 경보 목록에서 선택한 경보코드
    evtCode: { type: String, default: '1' },
    tableMinHeight: { type: String, default: '400px' },
  },
  data () {
    return {
      eventColor: EVENT_COLOR,
      eventCodeMap: null,
      tankMap: {},
      tankOpts: [],
      eventTanks: [],
      suppress: {
        tid: '',
        evtCode: '',
        min: 10,
      },
      suppressAlarmMin: 10,
      suspendOpts: [
        {value:2, text:'2 분'},
        {value:5, text:'5 분'},
        {value:10, text:'10 분'},
        {value:30, text:'30 분'},
        {value:60, text:'1 시간'},
        {value:180, text:'3 시간'},
        {value:360, text:'6 시간'},
        {value:720, text:'12 시간'},
        {value:1440, text:'24 시간'},
        {value:2880, text:'2 일'},
        {value:4320, text:'3 일'},
        {value:10080, text:'7 일'},
      ],
    }
  },
  computed: {
    // 알람지연 목록은 스토어 공유 (Aside 의 경보 필터링과 같은 값을 본다)
    suppressAlarms(){
      return this.$store.state.alarm.suppress;
    },
    // 표에 보여줄 지연 목록. 센서점검(8)은 경보판에서 다루지 않아 뺀다
    visibleSuppressAlarms(){
      const map = { ...(this.suppressAlarms || {}) };
      delete map[HIDDEN_EVENT_CODE];
      return map;
    },
  },
  watch: {
    evtCode(){
      this.getEventTankCount();
    },
  },
  async mounted() {
    try{
      // 로그인 직후에는 코드/탱크 기본정보 적재가 끝나지 않았을 수 있다.
      for(let i=0; i<20 && !this.$store.state.codeMaps['EVENT']; i++) await sleep(500);

      this.eventCodeMap = this.$store.state.codeMaps['EVENT'];
      this.tankMap = this.$store.state.tanks['map'] || {};
      this.tankOpts = this.$store.state.tanks['opts'] || [];

      await loadSuppressAlarms();
      await this.getEventTankCount();

    }catch(err){
      console.error(err);
    }
  },
  methods: {
    /** 선택된 경보의 탱크별 발생 건수 */
    async getEventTankCount(){
      try{
        const fromDays = this.$store.state.alarm.count.fromDays || 3;
        const query = qs.stringify({
          code: this.evtCode,
          fromDts: moment().subtract(fromDays, 'days').toISOString(),
          toDts: moment().toISOString(),
        });

        this.eventTanks = [];
        const {result} = await apiCall('get', `/api/event/tank-count?${query}`);
        if(result) this.eventTanks = result;

      }catch(err){
        console.error(err);
      }
    },

    /** 알람 확인/지연 설정 */
    async setSuppressAlarm(tid, evtCode, min=10){
      try{
        if(!tid) {
          this.toastWarn('탱크를 선택 하세요');
          return;
        }

        const r = await apiCall('put', `/api/alarm/suppress`, {
          tid,
          evtCode,
          min: min? min:10,
        });

        if(r.code===200) {
          await loadSuppressAlarms();
          const msg = '탱크 '+ this.tankMap[tid]?.name + ' ' + this.eventCodeMap[evtCode] + ', ' + min + "분 지연설정 완료";
          await this.toastInfo(msg, '경보 지연설정');
        }else{
          await this.toastResult(r);
        }

      }catch(err){
        console.error(err);
      }
    },
  }
}
</script>
